import { useEffect, useMemo, useState } from 'react';
import {
  Blocks, Check, Loader2, Plus, RotateCcw, Save, Search, Trash2, UserCheck, X,
} from 'lucide-react';
import { useStore } from '../../store';
import { salesCrmApi } from '../../services/salesCrmApi';
import type { FassungsBereich } from '../../types';
import { confirmDialog } from '../../components/ui/dialog';
import { splitSubsections, joinSubsections, subKey } from '../../lib/scriptSubsections';
import { TokenTextarea } from '../../components/TokenTextarea';
import { SCRIPT_TOKENS } from '../../lib/scriptTokens';
import type { LibraryScript, ScriptSection, ScriptSectionOption } from '../../types';

// ---------------------------------------------------------------------------
// Skript umbauen — Absatz für Absatz eine andere Fassung wählen, und innerhalb eines Absatzes
// auch Unterüberschrift für Unterüberschrift.
//
// Bis hierher war ein Bibliotheks-Skript ein Ganzes: übernehmen oder branchen, alles oder nichts.
// Wer die Eröffnung von Bianca und die Preis-Antwort von Manfred wollte, musste beide Skripte
// nebeneinanderlegen und von Hand zusammenkopieren.
//
// Zwei Ebenen, weil die Skripte zwei haben (gemessen 2026-08-19: 4 von 5 Bibliotheks-Skripten
// führen `###`): ein „Intro" besteht aus `### Who`, `### Why`, `### What`. Nur im Ganzen zu
// tauschen hiesse, für ein anderes „Why" ein fremdes „Who" mitzunehmen.
//
// Links das Skript als Absatzliste, rechts zu jedem Absatz die Fassungen, die es dafür schon gibt —
// aus der Main, aus JEDEM anderen eingestellten Skript, aus den geteilten Bausteinen. Der Vorrat
// wird also gelesen, nicht gepflegt: es gibt keine zweite Oberfläche, in der jemand Fassungen
// sammeln müsste (Prinzip 12). Wer eine neue schreibt, legt sie für alle daneben.
//
// Das Ergebnis ist ein NEUES Skript; das umgebaute bleibt, wie es war. Nur wer es selbst
// eingestellt hat (oder Admin ist) kann stattdessen ersetzen — dann geht die Fassung an alle
// weiter, die damit telefonieren (`resync_library_adopters` im Backend).
// ---------------------------------------------------------------------------

const SOURCE_LABEL: Record<ScriptSectionOption['source'], string> = {
  main: 'Main',
  library: 'Bibliothek',
  branch: 'Baustein',
  option: 'geschrieben',
};

const SOURCE_STYLE: Record<ScriptSectionOption['source'], string> = {
  main: 'text-slate-300 border-slate-500/40',
  library: 'text-accent border-accent/40',
  branch: 'text-blue-300 border-blue-400/40',
  option: 'text-emerald-300 border-emerald-400/40',
};

// Die ersten drei Bausteine als Beispiel im Hinweistext — ABGELEITET aus `SCRIPT_TOKENS`,
// nicht abgetippt. Eine zweite, von Hand gepflegte Liste lief in `ScriptLibrary.tsx` schon
// einmal auseinander: sie kannte [Nische] nicht.
const TOKEN_BEISPIELE = SCRIPT_TOKENS.slice(0, 3).map((t) => `[${t.key}]`).join(', ');

/** Eine getroffene Wahl: der Text und wo er herkam (Letzteres für die Notiz am neuen Skript). */
interface Wahl { body: string; herkunft: string }

export default function SkriptUmbau({
  entry,
  canReplace,
  onClose,
  onSaved,
}: {
  entry: LibraryScript;
  canReplace: boolean;
  onClose: () => void;
  onSaved: (hinweis: string) => void;
}) {
  const { user, addLibraryScript, updateLibraryScript } = useStore();
  const [options, setOptions] = useState<Record<string, ScriptSectionOption[]>>({});
  const [subOptions, setSubOptions] = useState<Record<string, Record<string, ScriptSectionOption[]>>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  // Zwei Ebenen, zwei Zustände. `basis` ist der ganze Absatz (kein Eintrag = das Original),
  // `subWahl` sind die einzelnen Unterüberschriften darin. Getrennt zu halten ist der Grund, warum
  // beides zusammenspielt: die Unterüberschriften wirken immer auf DEN Text, der gerade gilt.
  const [basis, setBasis] = useState<Record<string, Wahl>>({});
  const [subWahl, setSubWahl] = useState<Record<string, Record<string, Wahl>>>({});
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [name, setName] = useState(`${entry.name} (umgebaut)`);

  const order = useMemo(
    () => (entry.section_order?.length ? entry.section_order : Object.keys(entry.sections_data))
      .filter((k) => entry.sections_data[k]),
    [entry],
  );

  useEffect(() => {
    salesCrmApi
      .getLibrarySectionOptions(entry.id)
      .then((r) => { setOptions(r.options || {}); setSubOptions(r.sub_options || {}); })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, [entry.id]);

  useEffect(() => {
    if (!selectedKey && order.length) setSelectedKey(order[0]);
  }, [order, selectedKey]);

  const sichtbar = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return order;
    return order.filter((k) => {
      const sec = entry.sections_data[k];
      return `${sec?.title ?? ''} ${sec?.body ?? ''}`.toLowerCase().includes(q);
    });
  }, [order, entry, search]);

  /** Der Text, der für diesen Absatz gerade gilt: Grundfassung + die getauschten Unterüberschriften. */
  const effektiverBody = (key: string): string => {
    const original = entry.sections_data[key]?.body ?? '';
    const grund = basis[key]?.body ?? original;
    const wahl = subWahl[key];
    if (!wahl || Object.keys(wahl).length === 0) return grund;
    return joinSubsections(
      splitSubsections(grund).map((t) => {
        const w = t.title ? wahl[subKey(t.title)] : undefined;
        return w ? { ...t, body: w.body } : t;
      }),
    );
  };

  const istGeaendert = (key: string) =>
    effektiverBody(key).trim() !== (entry.sections_data[key]?.body ?? '').trim();

  const geaendert = order.filter(istGeaendert).length;

  // Den ganzen Absatz zu tauschen wirft die Wahl der Unterüberschriften weg: sie gehörten zum alten
  // Text. Sie stehen zu lassen hiesse, Sätze in einen Absatz zu tragen, in dem sie nie standen.
  const waehleBasis = (key: string, body: string, herkunft: string) => {
    setBasis((b) => ({ ...b, [key]: { body, herkunft } }));
    setSubWahl((s) => { const r = { ...s }; delete r[key]; return r; });
  };

  const zurueckAufOriginal = (key: string) => {
    setBasis((b) => { const r = { ...b }; delete r[key]; return r; });
    setSubWahl((s) => { const r = { ...s }; delete r[key]; return r; });
  };

  const waehleSub = (key: string, sk: string, body: string, herkunft: string) => {
    setSubWahl((s) => ({ ...s, [key]: { ...(s[key] ?? {}), [sk]: { body, herkunft } } }));
  };

  const subZurueck = (key: string, sk: string) => {
    setSubWahl((s) => {
      const je = { ...(s[key] ?? {}) };
      delete je[sk];
      const r = { ...s };
      if (Object.keys(je).length) r[key] = je; else delete r[key];
      return r;
    });
  };

  const neueSektionen = (): Record<string, ScriptSection> => {
    const out: Record<string, ScriptSection> = {};
    for (const key of order) {
      const sec = entry.sections_data[key];
      if (!sec) continue;
      // Titel und Bereich bleiben die des Absatzes — gewählt wird die FORMULIERUNG, nicht eine
      // andere Stelle im Gespräch. Sonst hiesse ein Skript nach dem Umbau an drei Stellen anders,
      // ohne dass jemand einen Titel geändert hätte.
      out[key] = { ...sec, body: effektiverBody(key) };
    }
    return out;
  };

  const herkunftsListe = (): string[] => Array.from(new Set([
    ...Object.entries(basis).filter(([k]) => istGeaendert(k)).map(([, w]) => w.herkunft),
    ...Object.values(subWahl).flatMap((je) => Object.values(je).map((w) => w.herkunft)),
  ]));

  const speichereNeu = async () => {
    const titel = name.trim();
    if (!titel) { setErr('Das neue Skript braucht einen Namen.'); return; }
    setSaving(true); setErr(null);
    try {
      const quellen = herkunftsListe();
      await addLibraryScript({
        name: titel,
        niche: entry.niche,
        language: entry.language,
        // Der Kanal des Skripts, aus dem umgebaut wurde. Ohne ihn landet ein umgebautes
        // Mail-Skript im Regal des Telefon-Skripts — der Server nimmt sonst 'skript' an.
        bereich: entry.bereich,
        sections_data: neueSektionen(),
        section_order: order,
        notes: `Umgebaut aus „${entry.name}" · ${geaendert} von ${order.length} Absätzen${
          quellen.length ? ` (${quellen.slice(0, 4).join(', ')}${quellen.length > 4 ? ' …' : ''})` : ''
        }`,
      });
      onSaved(`„${titel}" steht in der Bibliothek.`);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Der Weg, den die tägliche Arbeit geht: MEINE Fassung, ab dem nächsten Call, ohne zweiten
  // Eintrag in der Bibliothek und ohne dass jemand anderes etwas merkt. Der Rückweg steht im
  // Kopf der Bibliothek („Auf Team-Stand") und auf jeder fremden Karte („Übernehmen").
  const fuerMich = async () => {
    setSaving(true); setErr(null);
    try {
      const res = await salesCrmApi.applyToMyFork({
        niche: entry.niche,
        language: entry.language,
        bereich: entry.bereich,
        sections_data: neueSektionen(),
        section_order: order,
      });
      // Das Cockpit und der Skript-Tab hören auf dieses Ereignis und laden neu — sonst stünde dort
      // der alte Text, bis jemand die Seite wechselt.
      window.dispatchEvent(new CustomEvent('sales-script-updated'));
      onSaved(`Deine Fassung gilt ab dem nächsten Call · ${res.eigene_bloecke} eigene Absätze.`);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const ersetze = async () => {
    if (!(await confirmDialog({
      message: `„${entry.name}" mit dem Umbau überschreiben? Wer gerade mit diesem Skript telefoniert, bekommt die neue Fassung — eigene Änderungen an einzelnen Absätzen bleiben.`,
    }))) return;
    setSaving(true); setErr(null);
    try {
      const res = await updateLibraryScript(entry.id, {
        sections_data: neueSektionen(),
        section_order: order,
      });
      const n = res?.resynced ?? 0;
      onSaved(n ? `„${entry.name}" ersetzt · ${n} Arbeits-Skripte nachgezogen.` : `„${entry.name}" ersetzt.`);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const sec = selectedKey ? entry.sections_data[selectedKey] : null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-bg-surface border border-border-primary rounded-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border-primary shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-text-main flex items-center gap-2">
              <Blocks size={16} className="text-accent" /> Umbauen · {entry.name}
            </h3>
            <p className="text-[11px] text-text-muted mt-0.5">
              Absatz für Absatz eine andere Fassung wählen — innerhalb eines Absatzes auch einzelne
              Unterüberschriften. Aus der Main, aus den Skripten der anderen, oder selbst geschrieben.
            </p>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-main shrink-0">
            <X size={18} />
          </button>
        </div>

        {err && (
          <div className="mx-4 mt-3 text-[11px] text-danger bg-danger/10 border border-danger/30 rounded-md px-2.5 py-1.5 shrink-0">
            {err}
          </div>
        )}

        <div className="flex-1 flex min-h-0">
          {/* Links: die Absätze des Skripts */}
          <aside className="w-64 shrink-0 border-r border-border-primary flex flex-col min-h-0">
            <div className="p-2.5 border-b border-border-primary">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Absatz suchen…"
                  className="w-full bg-bg-input border border-border-primary rounded-lg pl-8 pr-2.5 py-1.5 text-xs text-text-main focus:outline-none focus:border-accent"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {sichtbar.map((key) => {
                const s = entry.sections_data[key];
                // Die Zahl zählt BEIDE Ebenen: sonst stünde an einem Absatz „nichts", obwohl es für
                // seine Unterüberschriften sehr wohl Fassungen gibt.
                const anzahl = (options[key]?.length ?? 0)
                  + Object.values(subOptions[key] ?? {}).reduce((n, l) => n + l.length, 0);
                const dran = istGeaendert(key);
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedKey(key)}
                    className={`w-full text-left px-2 py-1.5 rounded-lg text-[13px] flex items-center gap-2 transition-colors ${
                      selectedKey === key ? 'bg-accent/10 text-accent' : 'text-text-main hover:bg-bg-elevated'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dran ? 'bg-accent' : 'bg-slate-600'}`} />
                    <span className="flex-1 truncate">{s?.title || key}</span>
                    {anzahl > 0 && <span className="text-[10px] text-text-muted shrink-0">{anzahl}</span>}
                  </button>
                );
              })}
              {sichtbar.length === 0 && (
                <div className="px-2 py-6 text-center text-xs text-text-muted">Kein Absatz gefunden.</div>
              )}
            </div>
          </aside>

          {/* Rechts: die Fassungen dieses Absatzes */}
          <section className="flex-1 overflow-y-auto p-4 min-w-0">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-text-muted py-8 justify-center">
                <Loader2 size={16} className="animate-spin" /> Fassungen laden…
              </div>
            ) : !sec || !selectedKey ? (
              <div className="text-sm text-text-muted">Kein Absatz ausgewählt.</div>
            ) : (
              <AbsatzWahl
                key={selectedKey}
                sectionKey={selectedKey}
                sektion={sec}
                niche={entry.niche}
                language={entry.language}
                bereich={entry.bereich}
                optionen={options[selectedKey] ?? []}
                subOptionen={subOptions[selectedKey] ?? {}}
                aktuellerBody={effektiverBody(selectedKey)}
                basisWahl={basis[selectedKey] ?? null}
                subWahl={subWahl[selectedKey] ?? {}}
                userId={user?.id ?? null}
                isAdmin={user?.role === 'admin'}
                onBasis={(body, herkunft) => waehleBasis(selectedKey, body, herkunft)}
                onOriginal={() => zurueckAufOriginal(selectedKey)}
                onSub={(sk, body, herkunft) => waehleSub(selectedKey, sk, body, herkunft)}
                onSubZurueck={(sk) => subZurueck(selectedKey, sk)}
                onNeueOption={(opt) =>
                  setOptions((o) => ({ ...o, [selectedKey]: [opt, ...(o[selectedKey] ?? [])] }))
                }
                onNeueSubOption={(sk, opt) =>
                  setSubOptions((o) => ({
                    ...o,
                    [selectedKey]: {
                      ...(o[selectedKey] ?? {}),
                      [sk]: [opt, ...((o[selectedKey] ?? {})[sk] ?? [])],
                    },
                  }))
                }
                onOptionWeg={(optionId) =>
                  setOptions((o) => ({
                    ...o,
                    [selectedKey]: (o[selectedKey] ?? []).filter((x) => x.option_id !== optionId),
                  }))
                }
                onSubOptionWeg={(sk, optionId) =>
                  setSubOptions((o) => ({
                    ...o,
                    [selectedKey]: {
                      ...(o[selectedKey] ?? {}),
                      [sk]: ((o[selectedKey] ?? {})[sk] ?? []).filter((x) => x.option_id !== optionId),
                    },
                  }))
                }
                setErr={setErr}
              />
            )}
          </section>
        </div>

        {/* Fussleiste: was daraus wird */}
        <div className="border-t border-border-primary p-3 flex items-center gap-2 flex-wrap shrink-0">
          <span className="text-[11px] text-text-muted">
            {geaendert === 0
              ? `Noch nichts geändert · ${order.length} Absätze`
              : `${geaendert} von ${order.length} Absätzen geändert`}
          </span>
          <div className="flex-1" />
          {/* Drei Wege, absteigend nach Reichweite: nur ich · für alle daneben · für alle statt
              dessen. Der erste ist der tägliche und deshalb der hervorgehobene; die beiden anderen
              ändern etwas Geteiltes und stehen bewusst zurückhaltender daneben. */}
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name des neuen Skripts"
            className="w-52 bg-bg-input border border-border-primary rounded-lg px-3 py-1.5 text-xs text-text-main focus:outline-none focus:border-accent"
          />
          <button
            onClick={speichereNeu}
            disabled={saving || geaendert === 0}
            title="Als eigenes Skript in die Bibliothek stellen — für alle sichtbar, niemand bekommt es aufgedrängt"
            className="px-3 py-1.5 text-xs text-text-muted hover:text-text-main border border-border-primary rounded-lg disabled:opacity-40 flex items-center gap-1.5"
          >
            <Save size={13} /> Als neues Skript
          </button>
          {canReplace && (
            <button
              onClick={ersetze}
              disabled={saving || geaendert === 0}
              className="px-3 py-1.5 text-xs text-text-muted hover:text-text-main border border-border-primary rounded-lg disabled:opacity-40"
              title="Dieses geteilte Skript überschreiben — die Übernahmen der anderen ziehen nach"
            >
              Dieses ersetzen
            </button>
          )}
          <button
            onClick={fuerMich}
            disabled={saving}
            title="Nur für mich — gilt ab dem nächsten Call. Das geteilte Skript bleibt, wie es ist."
            className="px-3 py-1.5 bg-accent text-white rounded-lg text-xs font-medium hover:bg-accent/90 disabled:opacity-40 flex items-center gap-1.5"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <UserCheck size={13} />}
            Für mich übernehmen
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ein Absatz auf zwei Ebenen: im Ganzen tauschen, oder Unterüberschrift für Unterüberschrift.
// Die Umschaltung steht oben und erscheint nur, wenn es überhaupt `###` gibt.
// ---------------------------------------------------------------------------
function AbsatzWahl({
  sectionKey, sektion, niche, language, bereich, optionen, subOptionen, aktuellerBody, basisWahl,
  subWahl, userId, isAdmin, onBasis, onOriginal, onSub, onSubZurueck,
  onNeueOption, onNeueSubOption, onOptionWeg, onSubOptionWeg, setErr,
}: {
  sectionKey: string;
  sektion: ScriptSection;
  niche: string;
  language: string;
  /** Der Kanal des Skripts, das gerade umgebaut wird — er entscheidet, in welchen Vorrat eine
   *  eigene Fassung geschrieben und aus welchem sie zurückgezogen wird. */
  bereich: FassungsBereich;
  optionen: ScriptSectionOption[];
  subOptionen: Record<string, ScriptSectionOption[]>;
  aktuellerBody: string;
  basisWahl: Wahl | null;
  subWahl: Record<string, Wahl>;
  userId: number | null;
  isAdmin: boolean;
  onBasis: (body: string, herkunft: string) => void;
  onOriginal: () => void;
  onSub: (sk: string, body: string, herkunft: string) => void;
  onSubZurueck: (sk: string) => void;
  onNeueOption: (opt: ScriptSectionOption) => void;
  onNeueSubOption: (sk: string, opt: ScriptSectionOption) => void;
  onOptionWeg: (optionId: number) => void;
  onSubOptionWeg: (sk: string, optionId: number) => void;
  setErr: (e: string | null) => void;
}) {
  const teile = useMemo(() => splitSubsections(aktuellerBody), [aktuellerBody]);
  const hatSubs = teile.some((t) => t.title);
  // Die feinere Ebene zuerst, wo es sie gibt: sie ist der Grund, aus dem man dieses Fenster öffnet.
  const [ebene, setEbene] = useState<'sub' | 'block'>(hatSubs ? 'sub' : 'block');
  const [busy, setBusy] = useState(false);

  const darfWeg = (opt: ScriptSectionOption) =>
    opt.source === 'option' && (isAdmin || (userId != null && opt.created_by === userId));

  const entferne = async (opt: ScriptSectionOption, sk?: string) => {
    if (opt.option_id == null) return;
    if (!(await confirmDialog({ message: 'Diese Fassung zurückziehen?', tone: 'danger' }))) return;
    setBusy(true); setErr(null);
    try {
      await salesCrmApi.removeScriptSectionOption(opt.option_id, bereich);
      if (sk) onSubOptionWeg(sk, opt.option_id); else onOptionWeg(opt.option_id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 max-w-3xl">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-text-main">{sektion.title || sectionKey}</h4>
          <p className="text-[11px] text-text-muted font-mono">{sectionKey}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {busy && <Loader2 size={13} className="animate-spin text-text-muted" />}
          {(basisWahl || Object.keys(subWahl).length > 0) && (
            <button
              onClick={onOriginal}
              className="px-2 py-1 text-[11px] text-text-muted hover:text-text-main border border-border-primary rounded-md flex items-center gap-1"
              title="Diesen Absatz komplett auf das Original zurücksetzen"
            >
              <RotateCcw size={11} /> Original
            </button>
          )}
        </div>
      </div>

      {hatSubs && (
        <div className="flex gap-1 bg-bg-elevated rounded-lg p-1 w-fit">
          {([
            ['sub', `Unterüberschriften · ${teile.filter((t) => t.title).length}`],
            ['block', `Ganzer Absatz · ${optionen.length}`],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setEbene(id)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                ebene === id ? 'bg-accent text-white' : 'text-text-muted hover:text-text-main'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {ebene === 'sub' && hatSubs ? (
        <div className="space-y-3">
          {teile.map((teil, i) => {
            if (!teil.title) {
              // Der Vorspann trägt keine Überschrift und hat deshalb anderswo keine Entsprechung.
              // Er steht hier, damit der Absatz vollständig dasteht — tauschbar ist er über die
              // andere Ebene („Ganzer Absatz").
              return (
                <div key={`vorspann-${i}`} className="border border-dashed border-border-primary rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Vorspann</div>
                  <p className="text-[13px] leading-relaxed text-text-muted whitespace-pre-wrap">{teil.body}</p>
                </div>
              );
            }
            const sk = subKey(teil.title);
            return (
              <UnterabschnittWahl
                key={sk || `sub-${i}`}
                sectionKey={sectionKey}
                subSchluessel={sk}
                titel={teil.title}
                body={teil.body}
                original={
                  splitSubsections(sektion.body).find((t) => t.title && subKey(t.title) === sk)?.body ?? null
                }
                optionen={subOptionen[sk] ?? []}
                gewaehlt={!!subWahl[sk]}
                niche={niche}
                bereich={bereich}
                language={language}
                darfWeg={darfWeg}
                onWahl={(body, herkunft) => onSub(sk, body, herkunft)}
                onZurueck={() => onSubZurueck(sk)}
                onNeueOption={(opt) => onNeueSubOption(sk, opt)}
                onEntfernen={(opt) => entferne(opt, sk)}
                setErr={setErr}
              />
            );
          })}
        </div>
      ) : (
        <BlockWahl
          sectionKey={sectionKey}
          sektion={sektion}
          niche={niche}
          language={language}
          bereich={bereich}
          optionen={optionen}
          gewaehlt={basisWahl?.body ?? null}
          darfWeg={darfWeg}
          onWahl={onBasis}
          onZurueck={onOriginal}
          onNeueOption={onNeueOption}
          onEntfernen={(opt) => entferne(opt)}
          setErr={setErr}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ebene 1: der ganze `##`-Absatz.
// ---------------------------------------------------------------------------
function BlockWahl({
  sectionKey, sektion, niche, language, bereich, optionen, gewaehlt, darfWeg,
  onWahl, onZurueck, onNeueOption, onEntfernen, setErr,
}: {
  sectionKey: string;
  sektion: ScriptSection;
  niche: string;
  language: string;
  bereich: FassungsBereich;
  optionen: ScriptSectionOption[];
  gewaehlt: string | null;
  darfWeg: (opt: ScriptSectionOption) => boolean;
  onWahl: (body: string, herkunft: string) => void;
  onZurueck: () => void;
  onNeueOption: (opt: ScriptSectionOption) => void;
  onEntfernen: (opt: ScriptSectionOption) => void;
  setErr: (e: string | null) => void;
}) {
  return (
    <div className="space-y-2.5">
      <EigeneFassung
        sectionKey={sectionKey}
        titel={sektion.title || sectionKey}
        vorlage={gewaehlt ?? sektion.body ?? ''}
        niche={niche}
        language={language}
        bereich={bereich}
        onGespeichert={(opt) => { onNeueOption(opt); onWahl(opt.body, opt.source_label || 'eigene Fassung'); }}
        setErr={setErr}
      />

      <Fassung
        aktiv={gewaehlt == null}
        herkunft="Original"
        stil="text-text-muted border-border-primary"
        titel={null}
        autor={null}
        body={sektion.body}
        onWahl={onZurueck}
      />

      {optionen.map((opt) => (
        <Fassung
          key={opt.id}
          aktiv={gewaehlt != null && gewaehlt.trim() === (opt.body || '').trim()}
          herkunft={`${SOURCE_LABEL[opt.source]} · ${opt.source_label}`}
          stil={SOURCE_STYLE[opt.source]}
          // Der Titel der Fassung steht dabei, wenn er vom Absatz abweicht: die Zuordnung läuft über
          // Schlüssel UND Titel, und wer sieht, unter welcher Überschrift ein Text im anderen Skript
          // stand, erkennt eine unpassende Fassung sofort.
          titel={(opt.title || '').trim() !== (sektion.title || '').trim() ? opt.title : null}
          autor={opt.author}
          body={opt.body}
          onWahl={() => onWahl(opt.body, `${SOURCE_LABEL[opt.source]}: ${opt.source_label}`)}
          onEntfernen={darfWeg(opt) ? () => onEntfernen(opt) : undefined}
        />
      ))}

      {optionen.length === 0 && (
        <p className="text-[11px] text-text-muted border border-dashed border-border-primary rounded-lg px-3 py-4 text-center">
          Für diesen Absatz gibt es noch keine andere Fassung — schreib die erste.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ebene 2: EINE `###`-Unterüberschrift. Dieselbe Mechanik, kompakter und zugeklappt — von diesen
// stehen mehrere untereinander, und offen wären sie eine Wand.
// ---------------------------------------------------------------------------
function UnterabschnittWahl({
  sectionKey, subSchluessel, titel, body, original, optionen, gewaehlt, niche, language, bereich,
  darfWeg, onWahl, onZurueck, onNeueOption, onEntfernen, setErr,
}: {
  sectionKey: string;
  subSchluessel: string;
  titel: string;
  body: string;
  original: string | null;
  optionen: ScriptSectionOption[];
  gewaehlt: boolean;
  niche: string;
  language: string;
  bereich: FassungsBereich;
  darfWeg: (opt: ScriptSectionOption) => boolean;
  onWahl: (body: string, herkunft: string) => void;
  onZurueck: () => void;
  onNeueOption: (opt: ScriptSectionOption) => void;
  onEntfernen: (opt: ScriptSectionOption) => void;
  setErr: (e: string | null) => void;
}) {
  const [offen, setOffen] = useState(false);

  return (
    <div className={`border rounded-xl overflow-hidden ${gewaehlt ? 'border-accent/50' : 'border-border-primary'}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-bg-elevated/60 border-b border-border-primary">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${gewaehlt ? 'bg-accent' : 'bg-slate-600'}`} />
        <span className="text-[13px] font-medium text-text-main truncate">### {titel}</span>
        <div className="flex-1" />
        {gewaehlt && (
          <button
            onClick={onZurueck}
            title="Diese Unterüberschrift zurück auf das Original"
            className="text-[10px] text-text-muted hover:text-text-main flex items-center gap-1"
          >
            <RotateCcw size={10} /> Original
          </button>
        )}
        <button
          onClick={() => setOffen((v) => !v)}
          className="text-[11px] text-text-muted hover:text-accent border border-border-primary rounded-md px-2 py-0.5 shrink-0"
        >
          {offen ? 'Schliessen' : `Fassungen · ${optionen.length}`}
        </button>
      </div>

      <p className="px-3 py-2.5 text-[13px] leading-relaxed text-text-main whitespace-pre-wrap">
        {body.trim() || <span className="text-text-muted italic">Leer.</span>}
      </p>

      {offen && (
        <div className="border-t border-border-primary p-3 space-y-2.5 bg-bg-surface/40">
          <EigeneFassung
            sectionKey={sectionKey}
            subKey={subSchluessel}
            titel={titel}
            vorlage={body}
            niche={niche}
            language={language}
            bereich={bereich}
            onGespeichert={(opt) => { onNeueOption(opt); onWahl(opt.body, `${titel}: ${opt.source_label}`); }}
            setErr={setErr}
          />

          {original != null && (
            <Fassung
              aktiv={!gewaehlt}
              herkunft="Original"
              stil="text-text-muted border-border-primary"
              titel={null}
              autor={null}
              body={original}
              onWahl={onZurueck}
            />
          )}

          {optionen.map((opt) => (
            <Fassung
              key={opt.id}
              aktiv={gewaehlt && body.trim() === (opt.body || '').trim()}
              herkunft={`${SOURCE_LABEL[opt.source]} · ${opt.source_label}`}
              stil={SOURCE_STYLE[opt.source]}
              // Unter welcher Schreibweise die Fassung drüben stand („Why" vs. „Why:") — derselbe
              // Platz, aber nicht dasselbe Wort.
              titel={(opt.title || '').trim() !== titel.trim() ? opt.title : null}
              autor={opt.author}
              body={opt.body}
              onWahl={() => onWahl(opt.body, `${titel}: ${opt.source_label}`)}
              onEntfernen={darfWeg(opt) ? () => onEntfernen(opt) : undefined}
            />
          ))}

          {optionen.length === 0 && (
            <p className="text-[11px] text-text-muted text-center py-2">
              Für „{titel}" gibt es noch keine andere Fassung — schreib die erste.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// „Eigene Fassung schreiben" — auf beiden Ebenen dasselbe Formular, nur mit oder ohne `sub_key`.
// ---------------------------------------------------------------------------
function EigeneFassung({
  sectionKey, subKey: sk, titel, vorlage, niche, language, bereich, onGespeichert, setErr,
}: {
  sectionKey: string;
  subKey?: string;
  titel: string;
  vorlage: string;
  niche: string;
  language: string;
  bereich: FassungsBereich;
  onGespeichert: (opt: ScriptSectionOption) => void;
  setErr: (e: string | null) => void;
}) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const speichere = async () => {
    const body = text.trim();
    if (!body) { setErr('Die Fassung ist leer.'); return; }
    setBusy(true); setErr(null);
    try {
      const opt = await salesCrmApi.addScriptSectionOption({
        section_key: sectionKey,
        sub_key: sk,
        title: titel,
        body,
        label: label.trim() || undefined,
        niche,
        language,
        bereich,
      });
      onGespeichert(opt);
      setOffen(false); setText(''); setLabel('');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!offen) {
    return (
      <button
        onClick={() => { setOffen(true); setText(vorlage); }}
        className="w-full px-2.5 py-1.5 text-[11px] text-text-muted hover:text-accent border border-dashed border-border-primary rounded-lg flex items-center justify-center gap-1"
      >
        <Plus size={11} /> Eigene Fassung {sk ? `für „${titel}"` : 'schreiben'}
      </button>
    );
  }

  return (
    <div className="space-y-2 border border-accent/30 rounded-xl p-3 bg-bg-elevated/60">
      {/* Dasselbe Feld wie im Skript-Tab: `[` tippen öffnet die Bausteinliste. Ein von Hand
          getippter Baustein fällt sonst erst im Gespräch auf — dann liest jemand eckige Klammern
          vor. Hier wog das schwerer als anderswo: die Fassung, die man schreibt, steht danach für
          ALLE zur Wahl, ein Tippfehler wandert also weiter. */}
      <TokenTextarea
        value={text}
        onChange={setText}
        rows={6}
        autoFocus
        className="w-full bg-bg-input border border-border-primary rounded-lg px-3 py-2.5 text-sm text-text-main focus:outline-none focus:border-accent leading-relaxed"
      />
      {/* Der Auslöser muss man wissen, sonst nützt er nichts — die Zeile ist der einzige Hinweis
          darauf, und sie kostet eine Zeile. */}
      <p className="text-[10px] text-text-muted">
        <span className="font-mono text-text-main">[</span> tippen öffnet die Bausteine
        ({TOKEN_BEISPIELE}) — im Call werden sie mit den Daten des Leads gefüllt.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={'Etikett, z. B. „kurz" oder „für Geschäftsführer"'}
          className="flex-1 min-w-[160px] bg-bg-input border border-border-primary rounded-lg px-3 py-1.5 text-xs text-text-main focus:outline-none focus:border-accent"
        />
        <button
          onClick={speichere}
          disabled={busy}
          className="px-3 py-1.5 bg-accent text-white rounded-lg text-xs font-medium hover:bg-accent/90 disabled:opacity-50 flex items-center gap-1"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Speichern & wählen
        </button>
        <button onClick={() => setOffen(false)} className="px-2 py-1.5 text-text-muted hover:text-text-main text-xs">
          Abbrechen
        </button>
      </div>
      <p className="text-[10px] text-text-muted">
        Steht danach {sk ? `bei „${titel}"` : 'bei diesem Absatz'} für alle zur Wahl — im Skript wirkt
        sie erst, wenn jemand sie auswählt.
      </p>
    </div>
  );
}

function Fassung({
  aktiv, herkunft, stil, titel, autor, body, onWahl, onEntfernen,
}: {
  aktiv: boolean;
  herkunft: string;
  stil: string;
  titel: string | null;
  autor: string | null;
  body: string;
  onWahl: () => void;
  onEntfernen?: () => void;
}) {
  return (
    <div
      onClick={onWahl}
      className={`rounded-xl border p-3 cursor-pointer transition-colors ${
        aktiv ? 'border-accent bg-accent/5' : 'border-border-primary bg-bg-elevated/40 hover:border-accent/40'
      }`}
    >
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className={`px-1.5 py-0.5 rounded-md text-[10px] border ${stil}`}>{herkunft}</span>
        {titel && <span className="text-[10px] text-text-muted italic">stand dort unter „{titel}"</span>}
        {autor && <span className="text-[10px] text-text-muted">von {autor}</span>}
        <div className="flex-1" />
        {onEntfernen && (
          <button
            onClick={(e) => { e.stopPropagation(); onEntfernen(); }}
            title="Fassung zurückziehen"
            className="text-text-muted hover:text-danger"
          >
            <Trash2 size={12} />
          </button>
        )}
        {aktiv && <Check size={14} className="text-accent" />}
      </div>
      <p className="text-[13px] leading-relaxed text-text-main whitespace-pre-wrap">
        {body?.trim() || <span className="text-text-muted italic">Leer.</span>}
      </p>
    </div>
  );
}
